import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { TimerTarget } from '../types/activity';
import { SubRoute, TabId } from '../types/ui';
import { useAppState } from './AppStateContext';
interface ManualEntryState {
  open: boolean;
  templateId?: string;
}
interface ExceptionSheetState {
  open: boolean;
  memberId?: string;
}
interface UiValue {
  tab: TabId;
  setTab: (tab: TabId) => void;
  sub: SubRoute | null;
  openSub: (sub: SubRoute | null) => void;
  taskId: string | null;
  openTask: (id: string | null) => void;
  offerId: string | null;
  openOffer: (id: string | null) => void;
  timerSheetOpen: boolean;
  setTimerSheetOpen: (open: boolean) => void;
  pendingTarget: TimerTarget | null;
  requestTimerStart: (target: TimerTarget) => void;
  confirmTimerConflict: () => void;
  cancelTimerConflict: () => void;
  approvalId: string | null;
  openApproval: (id: string | null) => void;
  manualEntry: ManualEntryState;
  openManualEntry: (templateId?: string) => void;
  closeManualEntry: () => void;
  exceptionSheet: ExceptionSheetState;
  openException: (memberId?: string) => void;
  closeException: () => void;
}
const UiContext = createContext<UiValue | null>(null);
export function UiProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    isAdmin,
    family,
    viewer,
    startTimer
  } = useAppState();
  const [tab, setTabState] = useState<TabId>(isAdmin ? 'pregled' : 'danes');
  const [sub, setSub] = useState<SubRoute | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [offerId, setOfferId] = useState<string | null>(null);
  const [timerSheetOpen, setTimerSheetOpen] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<TimerTarget | null>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState<ManualEntryState>({
    open: false
  });
  const [exceptionSheet, setExceptionSheet] = useState<ExceptionSheetState>({
    open: false
  });

  // Ob menjavi družine ali člana začnemo na prvem zavihku vloge.
  useEffect(() => {
    setTabState(isAdmin ? 'pregled' : 'danes');
    setSub(null);
    setTaskId(null);
    setOfferId(null);
    setApprovalId(null);
    setTimerSheetOpen(false);
    setPendingTarget(null);
  }, [isAdmin, family.id, viewer.id]);
  const setTab = useCallback((t: TabId) => {
    setSub(null);
    setTabState(t);
    window.scrollTo({
      top: 0
    });
  }, []);
  const openSub = useCallback((s: SubRoute | null) => {
    setSub(s);
    window.scrollTo({
      top: 0
    });
  }, []);
  const requestTimerStart = useCallback((target: TimerTarget) => {
    const result = startTimer(target);
    if (result === 'conflict') setPendingTarget(target);
    setTaskId(null);
    setTimerSheetOpen(true);
  }, [startTimer]);
  const confirmTimerConflict = useCallback(() => {
    if (pendingTarget) startTimer(pendingTarget, true);
    setPendingTarget(null);
  }, [pendingTarget, startTimer]);
  const value: UiValue = {
    tab,
    setTab,
    sub,
    openSub,
    taskId,
    openTask: setTaskId,
    offerId,
    openOffer: id => {
      setTaskId(null);
      setOfferId(id);
    },
    timerSheetOpen,
    setTimerSheetOpen,
    pendingTarget,
    requestTimerStart,
    confirmTimerConflict,
    cancelTimerConflict: () => setPendingTarget(null),
    approvalId,
    openApproval: setApprovalId,
    manualEntry,
    openManualEntry: templateId => {
      setTaskId(null);
      setManualEntry({
        open: true,
        templateId
      });
    },
    closeManualEntry: () => setManualEntry({
      open: false
    }),
    exceptionSheet,
    openException: memberId => {
      setTaskId(null);
      setExceptionSheet({
        open: true,
        memberId
      });
    },
    closeException: () => setExceptionSheet({
      open: false
    })
  };
  return <UiContext.Provider value={value} data-id="element-1302" data-mp="3hkm2pgqgfw9o">{children}</UiContext.Provider>;
}
export function useUi(): UiValue {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi mora biti znotraj UiProvider');
  return ctx;
}